// MARK: NPM Modules
import React, { useEffect, useState } from "react";
import styled from "styled-components";
// MARK: Redux Specific
import { RootState } from "reduxFunctionality/index";
import { useSelector } from "react-redux";
// MARK: Types
import NavigationBarProps from "types/globals/navigationBar/navigationBarProps";
import ReduxStyleguide from "types/redux/styleguide";
// MARK: Functionality
import URI from "utils/common/uri";
// MARK: Components
import Hamburger from "./hamburger";
import Image from "components/common/media/image";
import Link from "components/common/links/standard";
import Typography from "components/common/typography";
import { Container, LogoContainer, Identity, Nav } from "../shared";
import SearchIcon from "components/common/search/searchIcon";
import PlusOrExit from "./plusOrExit";
// MARK: Functionality
import determineActiveHeader from "../shared/determineActiveHeader";
import determineActiveLink from "../shared/determineActiveLink";
import NavigationItem from "types/globals/navigationBar/navigationItem";
import CSSStyles from "enums/cssStyles";
// MARK: Styled Components
interface FlexProps {
  height: number;
}

const Flex = styled.div<FlexProps>`
  display: flex;
  width: 100%;
  height: ${({ height }) => height}px;
  justify-content: space-between;
  align-items: center;
`;

const FlexContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100vh;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;

interface MobileNavigationBarMenuProps {
  display: string;
  marginTop: number;
}

const Menu = styled.ul<MobileNavigationBarMenuProps>`
  display: ${({ display }) => display};
  height: 100%;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  margin-block-start: 0em;
  margin-block-end: 0em;
  margin-inline-start: 0em;
  margin-inline-end: 0em;
  padding-inline-start: 0em;
  margin-top: ${({ marginTop }) => marginTop}px;
`;

interface NavigationListItemProps {
  width: string;
  height: string;
  marginLeft: number;
  marginTop: number;
}

const NavigationListItem = styled.li<NavigationListItemProps>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ width }) => width};
  height: ${({ height }) => height};
  margin-left: ${({ marginLeft }) => marginLeft}px;
  margin-top: ${({ marginTop }) => marginTop}px;

  :hover {
    text-decoration: underline;
  }
`;

const HamburgerAndSearchContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

// MARK: React component
// Styleguide & Datapoints
const getDatapoints = (styleguide: ReduxStyleguide) => {
  const { colors } = styleguide;
  const { primary, background } = colors;
  return {
    color: primary,
    background: background,
  };
};
// Component
const MobileNavigationBar = ({ data, activeRoute }: NavigationBarProps) => {
  // MARK: React Redux Variables
  const styleguide: ReduxStyleguide = useSelector(
    (state: RootState) => state.styleguide
  );
  // MARK: State Variables
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [transition, setTransition] = useState<string>("none");
  const [activeSecondaryNavigationItem, setActiveSecondaryNavigationItem] =
    useState<NavigationItem | undefined>(undefined);
  // MARK: Use Effects
  useEffect(() => {
    setTimeout(() => {
      setTransition("none");
      if (activeSecondaryNavigationItem) {
        setActiveSecondaryNavigationItem(undefined);
      }
    }, 1000);
  }, [menuOpen]);
  // MARK: Variables
  const { navigationBarHeight, margin, isPortrait, gutter } = styleguide;
  const { color, background } = getDatapoints(styleguide);

  // MARK: Functionality
  const renderMenu = () => {
    return (
      <Menu
        display={isPortrait ? "flex" : "contents"}
        marginTop={margin}
        role={"menu"}
        itemScope
        itemType={"https://schema.org/SiteNavigationElement"}
      >
        {activeSecondaryNavigationItem === undefined
          ? renderPrimaryNavigationItems()
          : renderSecondaryNavigationItems()}
      </Menu>
    );
  };

  const renderPrimaryNavigationItems = () => {
    const { navigationItems } = data;

    var navItems: Array<any> = [];

    navigationItems.forEach((navigationItem, _) => {
      const header = navigationItem.header.toLowerCase();

      if (header !== "search") {
        const active = determineActiveHeader(navigationItem, activeRoute);
        const uri = navigationItem.pages[0].uri;

        switch (navigationItem.pages.length) {
          case 1:
            navItems.push(
              <NavigationListItem
                key={"navigation-bar-item-" + header.toLowerCase()}
                itemProp="name"
                role="menuitem"
                width={isPortrait ? "100%" : "calc(33% - " + gutter + "px)"}
                height={"50px"}
                marginTop={gutter / 2}
                marginLeft={isPortrait ? 0 : gutter / 2}
                onClick={() => {
                  if (active) {
                    setMenuOpen(false);
                  }
                }}
              >
                <Link title={header} to={uri === "__home__" ? "/" : URI(uri)}>
                  <Typography
                    className={isPortrait ? CSSStyles.medium : CSSStyles.small}
                    type={"span"}
                    color={active ? color : color}
                    hoverColor={color}
                    textDecoration={active ? "underline" : ""}
                  >
                    {header}
                  </Typography>
                </Link>
              </NavigationListItem>
            );
            break;
          default:
            navItems.push(
              <NavigationListItem
                key={"navigation-bar-item-" + header.toLowerCase()}
                itemProp="name"
                role="menuitem"
                width={isPortrait ? "100%" : "calc(33% - " + gutter + "px)"}
                height={"50px"}
                marginTop={gutter / 2}
                marginLeft={isPortrait ? 0 : gutter / 2}
                onClick={() => {
                  setActiveSecondaryNavigationItem(navigationItem);
                }}
              >
                <Typography
                  className={isPortrait ? CSSStyles.medium : CSSStyles.small}
                  type={"span"}
                  color={active ? color : color}
                  hoverColor={color}
                  textDecoration={active ? "underline" : ""}
                >
                  {header}
                </Typography>
                <PlusOrExit active={false} color={color} />
              </NavigationListItem>
            );
        }
      }
    });

    return navItems;
  };

  const renderSecondaryNavigationItems = () => {
    const items: any = [];
    if (activeSecondaryNavigationItem) {
      const header = activeSecondaryNavigationItem.header.toLowerCase();

      items.push(
        <NavigationListItem
          key={"secondary-navigation-bar-item-" + header}
          itemProp="name"
          role="menuitem"
          width={isPortrait ? "100%" : "calc(33% - " + gutter + "px)"}
          height={"50px"}
          marginTop={gutter / 2}
          marginLeft={isPortrait ? 0 : gutter / 2}
          onClick={() => {
            setActiveSecondaryNavigationItem(undefined);
          }}
        >
          <Typography
            className={isPortrait ? CSSStyles.medium : CSSStyles.small}
            type={"span"}
            color={color}
            hoverColor={color}
          >
            <b>{header}</b>
          </Typography>
          <PlusOrExit active={true} color={color} />
        </NavigationListItem>
      );

      activeSecondaryNavigationItem.pages.forEach((item, _) => {
        const active = determineActiveLink(item, activeRoute);
        const { title, uri } = item;
        items.push(
          <NavigationListItem
            key={"navigation-bar-item-" + title.toLowerCase()}
            itemProp="name"
            role="menuitem"
            width={isPortrait ? "100%" : "calc(33% - " + gutter + "px)"}
            height={"50px"}
            marginTop={gutter / 2}
            marginLeft={isPortrait ? 0 : gutter / 2}
            onClick={() => {
              if (active) {
                setMenuOpen(false);
              }
            }}
          >
            <Link title={title} to={uri === "__home__" ? "/" : URI(uri)}>
              <Typography
                className={isPortrait ? CSSStyles.medium : CSSStyles.small}
                type={"span"}
                color={active ? color : color}
                hoverColor={color}
                textDecoration={active ? "underline" : ""}
              >
                {title.toLowerCase()}
              </Typography>
            </Link>
          </NavigationListItem>
        );
      });
    }

    return items;
  };

  const userPressedMenu = () => {
    setTransition("0.6s cubic-bezier(0.19, 1, 0.22, 1)");
    setMenuOpen(!menuOpen);
  };

  // MARK: Render
  return (
    <Container
      height={menuOpen ? "100vh" : navigationBarHeight + "px"}
      margin={margin}
      backgroundcolor={background}
      transition={transition}
      mobile={true}
    >
      <FlexContainer>
        <Flex height={navigationBarHeight}>
          <Identity
            to="/"
            aria-label={"Tap to go to the home page."}
            dimension={navigationBarHeight}
          >
            <LogoContainer>
              <Image
                src={"/delasignBlack.webp"}
                alt={"delasign"}
                height={"100%"}
              />
            </LogoContainer>
          </Identity>
          <HamburgerAndSearchContainer>
            <Link title={"search"} to={URI("search")}>
              <SearchIcon allowsInteraction={true} />
            </Link>
            <Hamburger
              menuOpen={menuOpen}
              onHamburgerClick={() => {
                userPressedMenu();
              }}
            />
          </HamburgerAndSearchContainer>
        </Flex>
        <Nav
          position={"absolute"}
          role={"navigation"}
          height={"calc(100% - " + navigationBarHeight + "px)"}
          top={navigationBarHeight + "px"}
        >
          {renderMenu()}
        </Nav>
      </FlexContainer>
    </Container>
  );
};

export default MobileNavigationBar;
